// Days, hours, and both per-option cutoffs below are team-confirmed (2026-08-20).
// `timezone: 'America/New_York'` is deliberate: the office day is defined in
// Eastern *local* time, so the window tracks EST/EDT across DST rather than
// freezing to a fixed UTC offset. Observed US federal holidays are computed
// algorithmically (see getUsHolidays below).
export const BUSINESS_HOURS = {
    timezone: 'America/New_York',
    days: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'],
    start: '09:00',
    end: '17:00',
    // Per-option cutoffs (ET): the live contact options stop being offered
    // before the office actually closes, so a rep isn't started on a fresh
    // call/callback right at close. Both fall back to `end` if unset.
    callNowEnd: '16:30', // Call now hidden after 4:30 PM ET
    callbackEnd: '15:30', // Request a callback hidden after 3:30 PM ET
};

// --- US federal holiday computation -----------------------------------------
// Holidays the office observes are computed for any year from their rules, so
// there is no hardcoded list to lapse. Fixed-date holidays apply the federal
// observed-date shift (Saturday -> Friday, Sunday -> Monday); floating holidays
// (nth/last weekday of a month) never need shifting.

const pad = (n) => String(n).padStart(2, '0');
const isoDateOf = (y, m, d) => `${y}-${pad(m)}-${pad(d)}`;
// Day of week for a Y-M-D, computed in UTC so it never depends on the runtime's
// local timezone. 0 = Sunday ... 6 = Saturday.
const dayOfWeek = (y, m, d) => new Date(Date.UTC(y, m - 1, d)).getUTCDay();

// The `n`th occurrence of `weekday` (0=Sun..6=Sat) in month `m` of year `y`.
const nthWeekday = (y, m, weekday, n) => {
    const offset = (weekday - dayOfWeek(y, m, 1) + 7) % 7;
    return 1 + offset + (n - 1) * 7;
};

// The last occurrence of `weekday` in month `m` of year `y`.
const lastWeekday = (y, m, weekday) => {
    const daysInMonth = new Date(Date.UTC(y, m, 0)).getUTCDate();
    const offset = (dayOfWeek(y, m, daysInMonth) - weekday + 7) % 7;
    return daysInMonth - offset;
};

// Federal observed date for a fixed-date holiday: if it lands on Saturday it is
// observed the Friday before, if on Sunday the Monday after. Returns {y,m,d},
// which may fall in an adjacent year (e.g. Jan 1 on a Saturday -> prior Dec 31).
const observed = (y, m, d) => {
    const wd = dayOfWeek(y, m, d);
    const shift = wd === 6 ? -1 : wd === 0 ? 1 : 0;
    const dt = new Date(Date.UTC(y, m - 1, d + shift));
    return { y: dt.getUTCFullYear(), m: dt.getUTCMonth() + 1, d: dt.getUTCDate() };
};

const holidayCache = {};

// Returns a Set of 'YYYY-MM-DD' strings for the US holidays the office observes
// within the given calendar year. Memoized per year.
export const getUsHolidays = (year) => {
    if (holidayCache[year]) {
        return holidayCache[year];
    }
    const set = new Set();
    const add = (o) => set.add(isoDateOf(o.y, o.m, o.d));

    // New Year's Day (Jan 1), observed. The shift can push it into an adjacent
    // year, so include this year's Jan 1 only if it stays in-year, and next
    // year's Jan 1 only if it rolls back onto this Dec 31.
    const nyThis = observed(year, 1, 1);
    if (nyThis.y === year) add(nyThis);
    const nyNext = observed(year + 1, 1, 1);
    if (nyNext.y === year) add(nyNext);

    add({ y: year, m: 1, d: nthWeekday(year, 1, 1, 3) });   // MLK Jr. Day: 3rd Mon Jan
    add({ y: year, m: 5, d: lastWeekday(year, 5, 1) });     // Memorial Day: last Mon May
    add(observed(year, 7, 4));                              // Independence Day: Jul 4
    add({ y: year, m: 9, d: nthWeekday(year, 9, 1, 1) });   // Labor Day: 1st Mon Sep
    const thanksgiving = nthWeekday(year, 11, 4, 4);        // Thanksgiving: 4th Thu Nov
    add({ y: year, m: 11, d: thanksgiving });
    add({ y: year, m: 11, d: thanksgiving + 1 });           // Day after Thanksgiving (Fri)
    add(observed(year, 12, 25));                            // Christmas Day: Dec 25

    holidayCache[year] = set;
    return set;
};

export const isWithinBusinessHours = (date = new Date(), end = BUSINESS_HOURS.end) => {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: BUSINESS_HOURS.timezone,
        weekday: 'short',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    }).formatToParts(date).reduce((acc, part) => {
        acc[part.type] = part.value;
        return acc;
    }, {});

    if (!BUSINESS_HOURS.days.includes(parts.weekday)) {
        return false;
    }

    // Build a YYYY-MM-DD string from explicit date parts in the business
    // timezone (same pattern as the weekday/time parts above). Avoids relying
    // on the `en-CA` locale happening to emit ISO order.
    const dateParts = new Intl.DateTimeFormat('en-US', {
        timeZone: BUSINESS_HOURS.timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).formatToParts(date).reduce((acc, part) => {
        acc[part.type] = part.value;
        return acc;
    }, {});
    const isoDate = `${dateParts.year}-${dateParts.month}-${dateParts.day}`;

    if (getUsHolidays(Number(dateParts.year)).has(isoDate)) {
        return false;
    }

    const currentMinutes = (parseInt(parts.hour, 10) % 24) * 60 + parseInt(parts.minute, 10);
    const [startHour, startMinute] = BUSINESS_HOURS.start.split(':').map(Number);
    const [endHour, endMinute] = end.split(':').map(Number);

    return currentMinutes >= (startHour * 60 + startMinute) && currentMinutes < (endHour * 60 + endMinute);
};
